import { Tag, Typography } from '@quickbit/qb-design-system';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { SHOW_SECRETS } from 'pages/Docs/constants';
import QbPay from '../../images/qb-pay.png';
import { CodeBlock } from '../CodeBlock';
import { InlineCode } from '../InlineCode';
import { PageText } from './types';

const createOnrampFlowOrderExample = `{
  "amount": 100,
  "currency": "EUR",
  "callback_url": "https://my-domain.com/api/quickbit-callback",
  "redirect_urls": {
    "success": "https://my-domain.com/api/quickbit-redirect"
  },
  "user_data": {
    "country_code": "GB", // <-- This will streamline the identification process
    "email": "gimli.mcgloin@lotr.com" // <-- This identifies returning customers
  }
}`;

const createOnrampFlowOrderResponseExample = `{
  "amount": 100,
  "currency": "EUR",
  "uuid": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  "created_at": "2023-04-12T06:23:07.558Z",
  "checkout_url": "https://web.quickbit.com/top-up?receiveOrderPreviewId=afc65186-b966-4f11-98bc-b06ae85ac255",
  "reference_id": null,
  "status": "PENDING",
  "creation_amount": 100,
  "email": "gimli.mcgloin@lotr.com"
}`;

const createOnrampFlowOrderWithoutAmountResponseExample = `{
    "amount": null,
    "crypto_amount": null,
    "currency": null,
    "uuid": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
    "created_at": "2025-07-23T08:30:01.132077Z",
    "checkout_url": "https://web.quickbit.com/partial-orders/set-amount?orderId=9e99bd8e-8683-4a12-9fd8-9a9982a59bab",
    "status": "PENDING",
    "creation_amount": null,
    "reference_id": null,
    "errors": null,
    "email": null,
    "callbacks": null,
    "callback_url": null,
    "has_user_data": false
}`;

const createOnrampFlowOrderWithUserHomeAddressExample = `{
  "amount": 100,
  "currency": "EUR",
  "callback_url": "https://my-domain.com/api/quickbit-callback",
  "redirect_urls": {
    "success": "https://my-domain.com/api/quickbit-redirect"
  },
  "user_data": {
    "country_code": "GB",
    "email": "gimli.mcgloin@lotr.com",
    "home_address": {
      "street": "Bag End 1",
      "city": "Hobbiton",
      "zip": "12345",
      "country_code": "GB",
    }
  }
}`;

const createOnrampFlowOrderWithCardDetailsExample = `{
  "amount": 100,
  "currency": "EUR",
  "callback_url": "https://my-domain.com/api/quickbit-callback",
  "redirect_urls": {
    "success": "https://my-domain.com/api/quickbit-redirect"
  },
  "user_data": {
    "country_code": "GB",
    "email": "gimli.mcgloin@lotr.com"
  },
  "credit_card_data": {
    "card_holder": "Gimli McGloin",
    "encrypted_credit_card_number": "HKh94zsDkYLcVSvI+v57zN6nxC6kxoDOO4XyPZR6f/3EuE2s89QF7D+SphnnCBYpzMgLt/pPzh8duVC1E7NJKrZEfY8Zh5CtCGS0c+rYuKK8pX9RiNtAgyk/EenmWlD3CVynf8JPKNOK6K3cKaOeEv/eqDewyMSOtIpvXrMn3HX2/0pEci/q+Hm5ExnqagfwThpiNLM62xrhQkwzdJ/JDSpkPFgu1UfWhiSfRz81yvZ2/FId39eMTeIHZkruBcOcWFLQwqGXKp2l39ccdj5zLIWEe1aEX4Td2RfK2GTKf/jvwLCP/jk25DdDxDfsbPui/niMXBilUoTqDR0XlSjdrw==",
    "encrypted_cvv": "FtfvtGEVkOGRaInGI99tYnZLudTlKjGlAsBbX3cA2baSwaKwZOfE2CTUX26hWhgvTQCYhYViqKjWqA8MqRnndu9iLdAMTldswh4K4VjVoG2VPd5z7W3IVxlskWmyHrKViH1l1R+/8T+xOMAvDl5qNQRh0cXGYcqPrCVfw/gGl9mEBf+quczECQmf9EFhF9rJ7X1sN7Tx7caSPp1Tu8nZfL4xg6j4UzjBWsdtXxftO9Cj5bJH9Gh51dCNLrQJnobSJXBTsOjRXeD3HaUrsktUjDMG0i4uUj3qgLxGFsbNywF/4s/mZNLaInNNR+oI5eKS8DmM2rfNVQMtDoUcuvxOXw==",
    "expiry_month": "01", // <-- This needs to be 2 characters long 01-12
    "expiry_year": "2030" // <-- This needs to be 4 characters long
  }
}`;

const pythonEncryptExample = `import requests
from base64 import b64encode
from cryptography.hazmat.primitives import hashes, serialization
from cryptography.hazmat.primitives.asymmetric import padding, rsa

def encrypt_credit_card_details(credit_card_number, cvv):
    response = requests.get(
        "https://qb-pay.quickbit.com/api/pay/v1/encryption",
        headers={
          "Content-Type": "application/json",
          "x-api-key": "your-api-key-goes-here" # <-- Add your API key here
        }
    )
    public_key_string = response.json()["public_key"]
    public_key = serialization.load_pem_public_key(
        public_key_string.encode()
    )

    encrypted_credit_card_number = public_key.encrypt(
        credit_card_number.encode(),
        padding.PKCS1v15(),
    )
    encrypted_cvv = public_key.encrypt(
        cvv.encode(),
        padding.PKCS1v15(),
    )
    return (
        b64encode(encrypted_credit_card_number).decode("utf-8"),
        b64encode(encrypted_cvv).decode("utf-8")
    )
`;

const nodeEncryptExample = `import forge from 'node-forge';

const encryptCardData = async (creditCardNumber: string, cvv: string) => {
  const keyResponse = await fetch(
    'https://qb-pay.quickbit.com/api/pay/v1/encryption',
    { headers: {
      "Content-Type": "application/json",
      "x-api-key": "your-api-key-goes-here" // <-- Add your API key here
    }}
  ).json();

  const publicKey = forge.pki.publicKeyFromPem(keyResponse.public_key);
  const encryptedCreditCardNumber = publicKey.encrypt(
    data.creditCardNumber,
    'RSAES-PKCS1-V1_5'
  );
  const encryptedCvv = publicKey.encrypt(data.cvv, 'RSAES-PKCS1-V1_5');
  return [
    forge.util.encode64(encryptedCreditCardNumber),
    forge.util.encode64(encryptedCvv),
  ];
};
`;

const createOnrampFlowOrderWithAutoLoginExample = `{
  "amount": 100,
  "currency": "EUR",
  "callback_url": "https://my-domain.com/api/quickbit-callback",
  "redirect_urls": {
    "success": "https://my-domain.com/api/quickbit-redirect"
  },
  "user_data": {
    "auto_login_user": true,
    "first_name": "Gimli",
    "last_name": "McGloin",
    "email": "gimli.mcgloin@lotr.com",
    "birthdate": "1980-01-01",
    "country_code": "GB",
  }
}`;

const callbackExample = `{
  "order_id": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  "type": "COMPLETED",
  "reference_id": "9013AA"
}`;

const getOrderResponseExample = `{
  "amount": 100,
  "crypto_amount": null,
  "currency": "EUR",
  "uuid": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  "created_at": "2023-04-12T06:23:07.558Z",
  "checkout_url": "https://qb-pay.quickbit.com?order-id=3fa85f64-5717-4562-b3fc-2c963f66afa6",
  "reference_id": "9013AA",
  "status": "COMPLETED",
  "creation_amount": 100,
  "errors": [],
  "email": null,
  "card_details": {
    "masked_card_number": "535583******1234",
    "account_holder": "Roger Quickbitsson"
  },
  "ssn_information": {
    "ssn": "198001011234",
    "country": "SE"
  }
}`;

const ordersSearchRequestBodyExample = `// Goes inside request body
{
  "pagination": {
    "page": 1,
    "size": 1
  },
  "filters": {
    "created_from": "2024-04-25T18:30:00.000Z",
    "created_to": "2024-04-30T18:30:00.000Z"
  }
}`;

const getAllOrdersResponseExample = `{
  "orders": [
    {
      "amount": 100,
      "crypto_amount": null,
      "currency": "EUR",
      "uuid": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
      "created_at": "2023-04-12T06:23:07.558Z",
      "checkout_url": null,
      "reference_id": "9013AA",
      "status": "COMPLETED",
      "creation_amount": 100,
      "errors": [],
      "email": "roger.quickbitsson@quickbit.com",
      "card_details": {
        "masked_card_number": "535583******1234",
        "account_holder": "Roger Quickbitsson"
      },
      "ssn_information": {
        "ssn": "198001011234",
        "country": "SE"
      },
      "callback_url": "https://my-domain.com/api/quickbit-callback",
    },
  ],
  "aggregated_data": {
      "order_count": 1
  }
}`;

const cardExampleWithout3ds = `Card number: 5555 5555 5555 4444
Name: <Any name>
Expiry date: <Any future date>
CVV: <Any number>`;

const cardExampleWith3ds = `Card number: 4444 3333 2222 1111
Name: <Any name>
Expiry date: <Any future date>
CVV: <Any number>`;

const errorsList = `From Quickbit:
BLOCKCHAIN_CANCELLED - The crypto transaction was cancelled on the blockchain
PIQ_DEPOSIT_UNEXPECTED_ERROR - An error occurred when creating a buy request (using encrypted card details in the onramp flow)
PIQ_DEPOSIT_REQUEST_FAILED - The request didn't cause an exception, but failed when creating a buy request (using encrypted card details in the onramp flow)
PIQ_DEPOSIT_UNEXPECTED_TX_STATE - The deposit somehow ended up in a state that wasn't expected after creating a buy request (using encrypted card details in the onramp flow)
ORDER_TIMED_OUT - The order timed out according to the merchant configuration regarding how orders live.

From PaymentIQ:
ERR_SYSTEM_ERROR
ERR_FAILED_TO_CONNECT`;

const cryptoAmountExample = `{
    "amount": 123456,
    "currency_code": "BTC",
    "scale": 8
}`;

const cryptoAmountUsageExample = `const order = createOrder(/* input data */); // Assuming this function exists and creates an order
const cryptoAmount = order.crypto_amount;

if (cryptoAmount) {
  const { amount, currency_code, scale } = cryptoAmount;
  const formattedCryptoAmount = amount * Math.pow(10, -scale);
  console.log(\`Crypto Amount: \${formattedCryptoAmount} \${currency_code}\`); // Crypto Amount: 0.00123456 BTC
}
`;

export const onrampDocumentation: Array<PageText> = [
  {
    id: 'onramp',
    title: 'Onramp',
    page: 'integration',
    subPage: 'onramp',
    content: <Typography variant="heading1">Onramp</Typography>,
  },
  {
    id: 'onramp-1',
    title: 'Onramp',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The onramp flow is a way to increase Quickbit Pay conversion and let
          your customers purchase crypto from within your system. It works in a
          similar fashion to the regular Quickbit Pay checkout flow, but instead
          of the customer being sent to the Quickbit Pay checkout page, they are
          directed to the Quickbit Web App where they can create an account to
          purchase and complete their order, as well as do other cryptocurrency
          related things.
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'onramp-2',
    title: 'Onramp',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          To begin the Onramp checkout flow, an order must be created in the
          Quickbit Pay system in the same way as for a regular Quickbit Pay
          order. However, it is done by calling the{' '}
          <InlineCode>/api/pay/v1/orders/onramp</InlineCode> endpoint.
        </Typography>
        <Typography variant="body">
          An example request body looks like this:
        </Typography>
        <CodeBlock language="json" code={createOnrampFlowOrderExample} />
        <Typography variant="body">
          Providing any of the <b>optional</b> inputs in the <b>optional</b>{' '}
          <InlineCode>user_data</InlineCode> field in the request body will give
          the end user a more streamlined experience:
        </Typography>
        <ul>
          <li>
            <InlineCode>country_code</InlineCode> - By providing the country
            code for customers, the Onramp flow can immediately trigger the
            correct identity verification process and create a better customer
            experience.
          </li>
          <li>
            <InlineCode>email</InlineCode> - By providing the email for the
            customers, the Onramp flow can skip the identification process
            entirely for returning customers.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'onramp-3',
    title: 'Onramp',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The response is also of the same format as a regular Quickbit Pay
          order, with the exception of the checkout URL. This URL instead
          directs the customer to the Quickbit app. See the example below.
        </Typography>
        <CodeBlock
          language="json"
          code={createOnrampFlowOrderResponseExample}
        />
        <br></br>
      </>
    ),
  },
  {
    id: 'onramp-4',
    title: 'Onramp',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          To ensure the best possible customer experience and to avoid
          unexpected changes in crypto exchange rates, we recommend that you
          persist the order ID and the checkout URL upon creation of the order.
          For instance, a customer can accidentally close the browser or refresh
          the page after they have purchased the crypto, but before sending it
          to the address associated with the order. In such a scenario they will
          have a wallet with crypto in it but the order would still be
          incomplete. By persisting the order information, the customer can be
          redirected to the same transaction flow.
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'onramp-5',
    title: 'Onramp',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          The onramp flow will create orders in Bitcoin.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'get-payment-methods',
    title: 'Get payment methods',
    page: 'integration',
    content: <Typography variant="heading2">Payment methods</Typography>,
  },
  {
    id: 'get-payment-methods-1',
    title: 'Get payment methods',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          <br></br>
          To display available payment methods on the merchant site, use the{' '}
          <InlineCode>GET /api/pay/v1/payment-methods</InlineCode> endpoint.
          This returns a list of supported payment methods, each containing:
          <ul>
            <li>
              <InlineCode>image_url</InlineCode>: URL for the payment method
              icon
            </li>
            <li>
              <InlineCode>name</InlineCode>: Display name of the payment method
            </li>
            <li>
              <InlineCode>id</InlineCode>: Identifier to be passed to the order
              creation endpoint
            </li>
          </ul>
          When creating an order, you can pass the selected payment method using
          the <InlineCode>payment_method</InlineCode> parameter. The value
          should match one of the <InlineCode>id</InlineCode>s from the list
          returned by the above endpoint. <br></br>
          Currently supported methods:
          <ul>
            <li>
              <InlineCode>skrill</InlineCode>: Automatically opens the Skrill
              payment screen in the Quickbit app
            </li>
            <li>
              <InlineCode>default</InlineCode>: Displays the general Quickbit
              payment screen, allowing the user to choose their preferred method
            </li>
          </ul>
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-app',
    title: 'The Quickbit app',
    page: 'integration',
    subPage: 'onramp',
    content: <Typography variant="heading2">The Quickbit app</Typography>,
  },
  {
    id: 'the-quickbit-app-1',
    title: 'The Quickbit web app',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          When the web app is opened through the checkout URL in the response,
          it will be preconfigured to assist the customer in finishing their as
          seamlessly as possible. For instance, if the customer lacks the
          balance required in their Quickbit account, they will first be
          directed to purchase the correct amount of crypto before sending it to
          the address associated with the order. The customer can, however,
          interact with other parts of the web app if they choose to do so.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-app-2',
    title: 'The Quickbit app',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          There are multiple ways for a customer to identify themselves in the
          app. For Swedish, Norwegian and Finnish customers, specific national
          identification methods are available (such as the Swedish BankID). For
          all other customers, Onfido is used, which is a login method that
          verifies customers&apos; identities by having them upload ID documents
          such as passports.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-app-3',
    title: 'The Quickbit app',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The Onfido login method is a bit cumbersome and time consuming.
          Because of this, customers using this method will be asked to supply
          an email address and password after a successful login. For future
          login attempts, these two fields can then be used instead to create a
          smoother experience. If the email address of the customer is entered
          as part of the above mentioned request body, the email/password login
          method will be pre-selected for the customer.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-app-4',
    title: 'The Quickbit app',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          Due to limitations in the Onfido test environment, only the United
          Kingdom can be selected as the country code for testing purposes.
          However, the document used can be from any country (and does not need
          to be real). To cause some more confusion, the United Kingdom is not
          an available country in production for the Quickbit app.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-app-5',
    title: 'The Quickbit app',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          Due to blocking of third-party cookies starting in 2025, the Onramp
          Flow does not have support to be rendered in an iframe. This means
          that the only viable option is to redirect the user to the{' '}
          <InlineCode>checkout_url</InlineCode> in the order creation response.
          As a fallback, if the Onramp Flow were to be rendered in an iframe,
          the user will be presented with a button to open it in a new tab.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'create-an-order-without-amount',
    title: 'Create an order without specifying the amount',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <Typography variant="heading2">
        Create an order without specifying the amount
      </Typography>
    ),
  },
  {
    id: 'create-an-order-without-amount-1',
    title: 'Create an order without specifying the amount',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          You can create an onramp order without providing the amount and
          currency upfront. This is particularly useful when you want the
          customer to decide how much they wish to purchase. In this flow, the
          customer will first be directed to the Quickbit app, where they can
          manually enter the desired amount. Once that is done, the standard
          onramp process begins seamlessly. The API response format is the same
          as a typical Quickbit Pay order, with one key difference: the{' '}
          <InlineCode>checkout_url</InlineCode> in the response points to the
          Quickbit app, allowing the customer to input the amount directly.
          <br />
          <b>Note:</b> if leaving out the amount, then both the amount and
          currency must be left out.
        </Typography>
        <CodeBlock
          language="json"
          code={createOnrampFlowOrderWithoutAmountResponseExample}
        />
        <br></br>
      </>
    ),
  },
  {
    id: 'already-have-home-address',
    title: 'Already have home address details collected for your customer?',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <Typography variant="heading2">
        Already have home address details collected for your customer?
      </Typography>
    ),
  },
  {
    id: 'already-have-home-address-1',
    title: 'Already have home address details collected for your customer?',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          If you already have home address data collected for your customers,
          you can streamline the onramp flow by sending that data in the order
          creation request. See the example below. The home address details are
          needed for VISA and Mastercard transactions to be compliant. If not
          provided, the customer will be asked to provide this information in
          the Quickbit app.
        </Typography>
        <CodeBlock
          language="json"
          code={createOnrampFlowOrderWithUserHomeAddressExample}
        />
        <br></br>
      </>
    ),
  },
  {
    id: 'already-have-credit-card-details',
    title: 'Already have credit card details collected for your customers?',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <Typography variant="heading2">
        Already have credit card details collected for your customers?
      </Typography>
    ),
  },
  {
    id: 'already-have-credit-card-details-1',
    title: 'Already have credit card details collected for your customers?',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          If you already have credit card details collected for your customers,
          you can streamline the onramp flow even further by adding encrypted
          credit card details to the order creation request. See the example
          below.
        </Typography>
        <CodeBlock
          language="json"
          code={createOnrampFlowOrderWithCardDetailsExample}
        />
        <br />
      </>
    ),
  },
  {
    id: 'how-to-encrypt-card-details',
    title: 'How to encrypt card details',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <Typography variant="subtitle">How to encrypt card details</Typography>
    ),
  },
  {
    id: 'how-to-encrypt-card-details-1',
    title: 'How to encrypt card details',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          To encrypt the credit card details, you need to use a public
          encryption key from Quickbit. This can be accessed using the{' '}
          <InlineCode>GET /api/pay/v1/encryption</InlineCode> endpoint. This
          needs to be used to encrypt the credit card number and CVV.
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'how-to-encrypt-card-details-2',
    title: 'How to encrypt card details',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          Under the hood, Quickbit uses encryption parameters from{' '}
          <a
            href="https://worldline.com/en/home/main-navigation/solutions/merchants/payment-orchestration.html"
            target="_blank"
            rel="noreferrer"
          >
            PaymentIQ
          </a>
          , our payment service provider. PaymentIQ is fully PCI compliant, and
          Quickbit does not have access to their private key. This means that
          Quickbit cannot decrypt the credit card details, but will instead
          forward the encrypted card details to PaymentIQ where they will be
          processed for the purchase.
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'how-to-encrypt-card-details-3',
    title: 'How to encrypt card details',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The encryption algorithm is <b>RSA</b> with <b>PKCS1</b> padding. The
          resulting encrypted data should be base64 encoded. Below are examples
          of how to encrypt the credit card details using Python and Typescript.
        </Typography>
        <CodeBlock language="python" code={pythonEncryptExample} />
        <CodeBlock language="ts" code={nodeEncryptExample} />
      </>
    ),
  },
  ...(SHOW_SECRETS
    ? [
        {
          id: 'automatic-login-of-users',
          title: 'Automatic login of users',
          page: 'integration',
          subPage: 'onramp',
          content: (
            <>
              <br /> {/* This is because of the SHOW_SECRETS */}
              <Typography variant="heading2">
                Automatic login of users
              </Typography>
            </>
          ),
        },
        {
          id: 'automatic-login-of-users-1',
          title: 'Automatic login of users',
          page: 'integration',
          subPage: 'onramp',
          content: (
            <>
              <Typography variant="body">
                By providing information about the customer in the{' '}
                <InlineCode>user_data</InlineCode> field in the request body,
                the customer will be able to skip the identification process
                entirely and go straight to the payment. See the example order
                creation request data below.
              </Typography>
              <CodeBlock
                language="json"
                code={createOnrampFlowOrderWithAutoLoginExample}
              />
              <Typography variant="body">
                The response data will include a boolean field{' '}
                <InlineCode>has_user_data</InlineCode>. This field will be set
                to <InlineCode>true</InlineCode> if all the fields in the{' '}
                <InlineCode>user_data</InlineCode> field were provided in the
                order creation request body.
              </Typography>
            </>
          ),
        },
      ]
    : []),
  {
    id: 'onramp-crypto-amount',
    title: 'Understanding crypto amounts',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <br />
        <Typography variant="heading2">Understanding crypto amounts</Typography>
      </>
    ),
  },
  {
    id: 'onramp-crypto-amount-1',
    title: 'Understanding crypto amounts',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The order&apos;s <InlineCode>crypto_amount</InlineCode> field
          describes the amount in crypto that is expected to be paid for the
          order. That is, it is the <InlineCode>amount</InlineCode> field
          exchanged to the cryptocurrency. This might not be set initially, but
          will instead be available once a cryptocurrency has been chosen for
          the order. This is done by the customer in the Quickbit app.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'onramp-crypto-amount-format',
    title: 'Crypto amount format',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The <InlineCode>crypto_amount</InlineCode> field is formatted a bit
          differently than the regular <InlineCode>amount</InlineCode> field. It
          is an object that contains the amount, the currency code, and the
          scale of the amount. The scale is used to determine how many decimal
          places the amount has. For instance, if the scale is 8 then the last
          digit of the amount should be in the 8th decimal place. See an example
          object below.
        </Typography>
        <CodeBlock language="json" code={cryptoAmountExample} />
        <Typography variant="body">
          The scale is applied to the amount by multiplying the amount with 10
          to the power of the negative scale. An example in JavaScript on how to
          apply this can be found below.
        </Typography>
        <CodeBlock language="javascript" code={cryptoAmountUsageExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'rendering-the-checkout-page',
    title: 'Rendering the checkout page',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <br />
        <Typography variant="heading2">Rendering the checkout page</Typography>
      </>
    ),
  },
  {
    id: 'rendering-the-checkout-page-1',
    title: 'Rendering the checkout page',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          As is shown above, the data returned from creating an order contains a
          checkout URL. This URL leads to a checkout page that the customer will
          use to send money. For onramp orders, customers will use the Quickbit
          app to purchase cryptocurrency with fiat currency and complete their
          payment.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'rendering-the-checkout-page-2',
    title: 'Rendering the checkout page',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <div
          css={{
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'block',
            textAlign: 'center',
          }}
        >
          <img
            css={{
              width: '300px',
              marginBottom: '8px',
            }}
            src={QbPay}
            alt="The Quickbit Pay checkout page"
          />
          <Typography variant="captionMedium">
            An example of the checkout page.
          </Typography>
        </div>
        <br></br>
      </>
    ),
  },
  {
    id: 'rendering-the-checkout-page-3',
    title: 'Rendering the checkout page',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          For onramp orders, customers will be redirected to the Quickbit Pay
          checkout page where they can purchase cryptocurrency using the
          Quickbit app. Unlike pay orders, <InlineCode>iframes</InlineCode> are{' '}
          <b>not</b> supported in the Onramp Flow - customers must complete
          their purchase in a new window or tab.
        </Typography>
        <br></br>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          The onramp flow requires customers to use the Quickbit app for
          identity verification and fiat-to-crypto conversion.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'callback-events',
    title: 'Callback events',
    page: 'integration',
    subPage: 'onramp',
    content: <Typography variant="heading2">Callback events</Typography>,
  },
  {
    id: 'callback-events-1',
    title: 'Callback events',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The Quickbit Pay system will notify you on the specified{' '}
          <InlineCode>callback_url</InlineCode> when the onramp order is either
          cancelled or completed. Since onramp orders use the Quickbit app for
          payment, the callback event will be created as soon as the customer
          has completed their purchase through the app.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'callback-events-2',
    title: 'Callback events',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The callback event will contain a body with the following properties:
          the order ID, the callback type, as well as the reference ID if that
          was provided. An example body is shown below.
        </Typography>
        <CodeBlock language="json" code={callbackExample} />
        <Typography variant="body">
          The order ID is added for you to determine which order we are
          notifying you about. This will be the same ID as the{' '}
          <InlineCode>uuid</InlineCode> returned in the response when creating
          orders. Similarly, the reference ID is added (if you provided one) for
          the same reason.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'callback-events-3',
    title: 'Callback events',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          Finally, the type of the callback determines what we are notifying you
          about. The following values are possible:
        </Typography>
        <ul>
          <li>
            <InlineCode>COMPLETED</InlineCode> - This means the customer has
            successfully completed their purchase through the Quickbit app. The
            money has reached the Quickbit system and will be sent to you in the
            next settlement. The remainder of your checkout can proceed without
            risk, such as sending a confirmation email or giving the product to
            the customer. If you specified a{' '}
            <InlineCode>redirect_url</InlineCode> when creating the order, the
            checkout page will redirect to that URL when this event is sent.
          </li>
          <li>
            <InlineCode>CANCELLED</InlineCode> - This means the order has been
            cancelled by the Quickbit Pay system. This can, for instance, be
            used to remove the reservation of a product in your system. It is
            possible to configure when orders should be cancelled using the
            Quickbit backoffice. More about this can be read{' '}
            <HashLink to="/docs/backoffice">here</HashLink>.
          </li>
          <li>
            <InlineCode>ERROR_OCCURRED</InlineCode> - This means the order
            received an error. This could depend on many different things, such
            as the customer using an invalid payment method or issues with
            identity verification. It should however be noted that an error
            occurring does not mean that the order has failed. It is possible
            for an order to have an error and still be completed successfully.
            The errors can be found in the <InlineCode>errors</InlineCode> field
            of the order. They can also be found in the Quickbit backoffice.
            More about this can be read in the{' '}
            <HashLink to="/docs/backoffice">Backoffice</HashLink> section.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'callback-events-4',
    title: 'Callback events',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          When a callback is sent from the Quickbit Pay system, the order status
          will be the same status as the callback type unless it is the{' '}
          <InlineCode>ERROR_OCCURRED</InlineCode> type.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'simulating-callbacks-for-testing-purposes',
    title: 'Simulating callbacks for testing purposes',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <Typography variant="subtitle">
        Simulating callbacks for testing purposes
      </Typography>
    ),
  },
  {
    id: 'simulating-callbacks-for-testing-purposes-1',
    title: 'Simulating callbacks for testing purposes',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          To test the onramp flow, you can simulate status changes for orders
          into the different callback states. This can be done in two ways:
          either by using the{' '}
          <InlineCode>
            {'POST /api/pay/v1/order/{order_id}/simulate-callback'}
          </InlineCode>{' '}
          simulation endpoint, or by using the backoffice to update the order.
          This will also update the order, meaning that the Quickbit Pay
          checkout page will be updated depending on which status the order got
          instead.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'simulating-callbacks-for-testing-purposes-2',
    title: 'Simulating callbacks for testing purposes',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The simulation endpoint and backoffice actions are only available in
          the Quickbit Pay test environment.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'fetching-an-order',
    title: 'Fetching an order',
    page: 'integration',
    subPage: 'onramp',
    content: <Typography variant="heading2">Fetching an order</Typography>,
  },
  {
    id: 'fetching-an-order-1',
    title: 'Fetching an order',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          In case an order needs to be retrieved in the future you can do so
          using the{' '}
          <InlineCode>{'GET /api/pay/v1/order/{order_id}'}</InlineCode>{' '}
          endpoint. As can be seen, the endpoint requires the order ID. This is
          yet another reason why it is important to store the{' '}
          <InlineCode>uuid</InlineCode> returned when creating orders.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'fetching-an-order-2',
    title: 'Fetching an order',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The endpoint for fetching orders will return the same type of response
          as the endpoint for creating orders. However, it may also include card
          details and or SSN information if access to this information is part
          of your contract with Quickbit, and if this information is available.
          An example can be seen below.
        </Typography>
        <CodeBlock language="json" code={getOrderResponseExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'fetching-all-orders',
    title: 'Fetching all orders',
    page: 'integration',
    subPage: 'onramp',
    content: <Typography variant="heading2">Fetching all orders</Typography>,
  },
  {
    id: 'fetching-all-orders-1',
    title: 'Fetching all orders',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          If you wish to get all orders created from a certain time, you can do
          so using the <InlineCode>POST /api/pay/v1/orders/search</InlineCode>{' '}
          endpoint.
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'fetching-all-orders-2',
    title: 'Fetching all orders',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The endpoint for fetching all orders supports pagination and filtering
          which means you can pass these options in a JSON body in the request.
          To make the response paginated, provide a{' '}
          <InlineCode>page</InlineCode> and <InlineCode>size</InlineCode> in a{' '}
          <InlineCode>pagination</InlineCode> object inside JSON body in
          request. The endpoint supports filtering the response based on
          creation time. In case you only need orders for a certain time period,
          provide a time in <InlineCode>filters</InlineCode> object inside the
          JSON body in the request via the <InlineCode>created_from</InlineCode>{' '}
          and <InlineCode>created_to</InlineCode> property. Alternatively you
          can pass only <InlineCode>created_from</InlineCode> to get all orders
          from a specific date and time, or <InlineCode>created_to</InlineCode>{' '}
          to get all orders up to a specific date and time. There are other
          filters that you can include. For more detailed information, see{' '}
          <Link to="/docs/endpoints">Endpoints</Link>. Below is an example of
          all the supported options which you can send in the request body.
        </Typography>
        <CodeBlock language="json" code={ordersSearchRequestBodyExample} />
        <br />
      </>
    ),
  },
  {
    id: 'fetching-all-orders-3',
    title: 'Fetching all orders',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          The response is an object with an <InlineCode>orders</InlineCode> list
          containing all orders created by merchants under the same platform if
          a platform is making the request or all orders of a single merchant if
          a merchant is making the request. The{' '}
          <InlineCode>aggregated_data</InlineCode> contains information about
          the total number of orders. An example response can be found below.
        </Typography>
        <CodeBlock language="json" code={getAllOrdersResponseExample} />
        <br />
      </>
    ),
  },
  {
    id: 'paying-with-the-quickbit-app-in-the-test-environment',
    title: 'Paying with the Quickbit app in the test environment',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <Typography variant="heading2">
        Paying with the Quickbit app in the test environment
      </Typography>
    ),
  },
  {
    id: 'paying-with-the-quickbit-app-in-the-test-environment-1',
    title: 'Paying with the Quickbit app in the test environment',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          Onramp orders require customers to use the Quickbit app for purchasing
          cryptocurrency with fiat currency. This app uses identity verification
          methods and allows purchasing of cryptocurrencies using a credit card.
          Because of this, some setup is required to make it work in the test
          environment (where real identity verification is not possible and real
          credit cards do not work).
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'paying-with-the-quickbit-app-in-the-test-environment-2',
    title: 'Paying with the Quickbit app in the test environment',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          There are multiple identity verification methods available, depending
          on the country used for logging in:
        </Typography>
        <ul>
          <li>
            <Typography variant="body">
              The method with the least setup required is Onfido (used for the
              United Kingdom), simply follow the login steps to setup an
              account. The document used for verification does not matter in the
              test environment. To make the account persist, this method will
              require you to set an email and password for future login
              attempts.
            </Typography>
          </li>
          <li>
            <Typography variant="body">
              A method requiring some setup is BankID (used for Sweden). This
              method requires a Swedish Test BankID which can be set up{' '}
              <a href="https://demo.bankid.com/">here</a>. Once setup, simply
              login using the personal number you generated, as well as the
              BankID app. Note that a test BankID app and production BankID app
              cannot be installed on the same device.
            </Typography>
          </li>
        </ul>
        <br />
      </>
    ),
  },
  {
    id: 'paying-with-the-quickbit-app-in-the-test-environment-3',
    title: 'Paying with the Quickbit app in the test environment',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          Card details that can be used in the test environment are available{' '}
          <a href="https://developer.worldpay.com/products/access/card-payments/testing/">
            here
          </a>
          . Cards may not work with all setups, so we recommend using any of the
          following two cards:
        </Typography>
        <br />
        <Typography variant="body">Without 3DS:</Typography>
        <CodeBlock language="txt" code={cardExampleWithout3ds} />
        <Typography variant="body">With 3DS:</Typography>
        <CodeBlock language="txt" code={cardExampleWith3ds} />
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          Some specific values can be used to trigger errors in the card
          purchase flows. These values are available{' '}
          <a href="https://developer.worldpay.com/products/access/card-payments/testing/">
            here
          </a>
          .
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'payouts',
    title: 'Payouts',
    page: 'integration',
    subPage: 'onramp',
    content: <Typography variant="heading2">Payouts</Typography>,
  },
  {
    id: 'payouts-1',
    title: 'Payouts',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          Payouts enable you as a merchant to send money in a cryptocurrency to
          your customer. This feature is currently in development and will be
          explained in more detail once it is available.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'errors',
    title: 'Errors',
    page: 'integration',
    subPage: 'onramp',
    content: <Typography variant="heading2">Errors</Typography>,
  },
  {
    id: 'errors-1',
    title: 'Errors',
    page: 'integration',
    subPage: 'onramp',
    content: (
      <>
        <Typography variant="body">
          As mentioned in the{' '}
          <HashLink to="/docs/integration/onramp#callback-events">
            Callback events
          </HashLink>{' '}
          section, errors can occur when processing an onramp order. Most errors
          in the onramp flow are propagated directly from{' '}
          <a
            href="https://worldline.com/en/home/main-navigation/solutions/merchants/payment-orchestration.html"
            target="_blank"
            rel="noreferrer"
          >
            PaymentIQ
          </a>
          , our payment service provider, when the user pays using the Quickbit
          app. Others are generated by Quickbit. Below is a list of the possible
          error codes. For the errors generated by Quickbit, there are also
          matching descriptions.
          <CodeBlock code={errorsList} language="txt" />
        </Typography>
      </>
    ),
  },
];
